import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from './auth.service';
import { Observable, catchError, mergeMap, of, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { AuthEnum } from '../enums/auth.enum';
import { NxWebComponentService } from '../../shared/services/nx-web-component/nx-web-component.service';
import { LocalStorageEnum } from '../enums/token.enum';
import { environment } from '../../../environments/environment'; // 🌟 Importa tu environment dinámico por entorno

const resourceUrl = 'https://devmavalue.plataforma-smart.com';
const excludedUrls = [
  'es.json',
  'it.json',
  'en.json',
  'de.json',
  'fr.json',
  `${resourceUrl}/${AuthEnum.LOGIN}`,
  'https://mocki.io/v1/4a78e17b-c76b-43cc-b9aa-598d82a29d0c',
  'garaje-de-ideas-multiasistencia-dev-i9demy4g.s3.eu-west-1.amazonaws.com/assets/',
  'mavalues3.s3.eu-west-1.amazonaws.com/assets'
];

function getToken(authService: AuthService): Observable<string> {
  return of(authService.getLocalStorageAccessToken());
}

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const excludedUrlsRegex = excludedUrls.map((urlPattern) => new RegExp(urlPattern)) || [];
  const authService = inject(AuthService);
  const webComponentService = inject(NxWebComponentService);
  const router = inject(Router);
  let request = req;

  // 1. Verificar exclusiones
  const isUserEndpoint = req.url.includes('api-token-auth/user') || req.url.includes('api-token-auth/usersso');
  const isLoginUrl = req.url.includes('api-token-auth') && !isUserEndpoint;
  
  const passThrough = isLoginUrl || !!excludedUrlsRegex.find((regex) => regex.test(req.url));
  if (passThrough) {
    return next(req);
  }

  // 2. 🌟 DETERMINACIÓN DINÁMICA DEL TIPO DE AUTORIZACIÓN (Mavalue -> Token, Billma -> Bearer)
  let authorizationWord = 'Bearer'; // Por defecto

  // Si es ruta absoluta y apunta al dominio Mavalue del entorno actual
  if (environment.apiUrl && req.url.includes(environment.apiUrl)) {
    authorizationWord = 'Token';
  }
  // Si es ruta absoluta y apunta al dominio Billma del entorno actual
  else if (environment.apiBillma && req.url.includes(environment.apiBillma)) {
    authorizationWord = 'Bearer';
  }
  // RUTAS RELATIVAS (Ej: "/api/ndbx", "/api/schedule")
  else {
    const mavalueKeywords = [
      'api/menus',
      'api/forms',
      'api/api-token-auth',
      'api/ndbx',
      'api/claimData',
      'api/pdf/damages',
      'api/gag/materials',
      'api/pdf/material',
      'api/assets/browser',
      'api/damages/repType',
      'api/furniture/upptec',
      'api/damages/update',
      'api/schedule',
      'api/fnol',
      'api-token-auth/user',
      'api-token-auth/usersso'
    ];

    const isMavalueRelative = mavalueKeywords.some((keyword) => req.url.includes(keyword));
    if (isMavalueRelative) {
      authorizationWord = 'Token';
    }
  }

  // 3. Flujo de clonación con el Token inyectado
  return getToken(authService).pipe(
    mergeMap((token: string) => {
      const lang =
        localStorage.getItem(LocalStorageEnum.SELECTED_LANG) ?? localStorage.getItem(LocalStorageEnum.LANGUAGE) ?? 'fr';

      request = req.clone({
        setHeaders: {
          'Content-Type': 'application/json',
          'Accept-Language': lang,
          Authorization: `${authorizationWord} ${token}`
        }
      });
      return next(request);
    }),
    catchError((error: HttpErrorResponse) => {
      // Control del 401
      if (error.status === 401) {
        if (authService.isFromLogin()) {
          authService.clear();
          return throwError(() => error); // Evita redirección si falla el propio login
        }

        const redirectionUrl = localStorage.getItem(LocalStorageEnum.USER_AUTH) ? 'auth/login' : 'login';

        webComponentService.isWebComponentFlow()
          ? webComponentService.hasTokenExpired.set(true)
          : router.navigateByUrl(redirectionUrl);
      }

      return throwError(() => error);
    })
  );
};
